import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { ApiError } from '@/shared/api/api-error';

import { useCreateRegion, useUpdateRegion } from '../api/regions.queries';
import { regionSchema, type RegionFormValues } from '../schemas/region.schema';
import type { Region } from '../types/region.types';

/**
 * Create / edit a region. Mount it only while open (the page does), so the
 * form always starts from the region being edited.
 */
export function RegionFormDialog({ region, onClose }: { region?: Region; onClose: () => void }) {
  const createRegion = useCreateRegion();
  const updateRegion = useUpdateRegion();
  const mutation = region ? updateRegion : createRegion;
  const isEdit = region !== undefined;

  const form = useForm<RegionFormValues>({
    resolver: zodResolver(regionSchema),
    defaultValues: {
      name: region?.name ?? '',
      arName: region?.arName ?? '',
      code: region?.code ?? '',
    },
  });

  // A code in use comes back as a field error on code.
  const { error } = mutation;
  useEffect(() => {
    if (!(error instanceof ApiError) || !error.fieldErrors) return;
    for (const [field, message] of Object.entries(error.fieldErrors)) {
      const key = (field.charAt(0).toLowerCase() + field.slice(1)) as keyof RegionFormValues;
      if (key in form.getValues()) form.setError(key, { type: 'server', message });
    }
  }, [error, form]);

  const onSubmit = (values: RegionFormValues) => {
    const payload = {
      name: values.name.trim(),
      arName: values.arName.trim(),
      code: values.code.trim(),
    };
    if (region) {
      updateRegion.mutate({ regionId: region.id, payload }, { onSuccess: onClose });
    } else {
      createRegion.mutate(payload, { onSuccess: onClose });
    }
  };

  const isPending = mutation.isPending;

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-base">
            {isEdit ? `Edit ${region.name}` : 'Add region'}
          </DialogTitle>
          <DialogDescription className="text-xs">
            A region is a brand&rsquo;s store (US, UAE…). Its denominations are grouped under it on
            the storefront.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
            <div className="grid gap-5 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Name</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder="United States"
                        autoFocus
                        disabled={isPending}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="arName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Arabic name</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        dir="rtl"
                        placeholder="الولايات المتحدة"
                        disabled={isPending}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="code"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Code</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder="US"
                        className="font-mono text-xs"
                        disabled={isPending}
                      />
                    </FormControl>
                    <FormDescription>Unique short identifier.</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={onClose} disabled={isPending}>
                Cancel
              </Button>
              <Button type="submit" disabled={isPending || !form.formState.isDirty}>
                {isPending && <Loader2 className="size-4 animate-spin" aria-hidden />}
                {isEdit ? 'Save changes' : 'Create region'}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
